import { useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { Banknote, CreditCard, Landmark, Gift, Split, Trash2, Plus, LogIn } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const METODOS = [
  { id: "efectivo", label: "Efectivo", icon: Banknote },
  { id: "tarjeta_debito", label: "T. Débito", icon: CreditCard },
  { id: "tarjeta_credito", label: "T. Crédito", icon: CreditCard },
  { id: "transferencia", label: "Transferencia", icon: Landmark },
  { id: "personalizado", label: "Personalizado", icon: Gift },
];
const DENOMINACIONES = [100, 200, 500, 1000];

export default function PaymentModal({ open, onClose, total, onConfirm, vendedores = [], vendedorId, onVendedor, onCheckin }) {
  const [pinPara, setPinPara] = useState(null);
  const [pin, setPin] = useState("");
  const [pinBusy, setPinBusy] = useState(false);
  async function confirmarPin(e) {
    e.preventDefault();
    if (!pinPara) return;
    setPinBusy(true);
    const ok = await onCheckin(pinPara, pin);
    setPinBusy(false);
    if (ok) { setPinPara(null); setPin(""); } else setPin("");
  }
  const [modo, setModo] = useState("efectivo");
  const [todosVend, setTodosVend] = useState(false);
  const [recibido, setRecibido] = useState("");
  const [referencia, setReferencia] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [mixto, setMixto] = useState([{ metodo: "efectivo", monto: "", descripcion: "" }]);

  const cambio = Math.max(0, (parseFloat(recibido) || 0) - total);
  const { sumaMixto, restante, cambioMixto } = useMemo(() => {
    const suma = mixto.reduce((s, p) => s + (parseFloat(p.monto) || 0), 0);
    const efectivo = mixto.filter((p) => p.metodo === "efectivo").reduce((s, p) => s + (parseFloat(p.monto) || 0), 0);
    return { sumaMixto: suma, restante: Math.max(0, total - suma), cambioMixto: Math.min(efectivo, Math.max(0, suma - total)) };
  }, [mixto, total]);
  const setFilaMixto = useCallback((i, campo, valor) => setMixto((prev) => prev.map((x, j) => (j === i ? { ...x, [campo]: valor } : x))), []);

  function reset() {
    setModo("efectivo"); setRecibido(""); setReferencia(""); setDescripcion("");
    setMixto([{ metodo: "efectivo", monto: "", descripcion: "" }]);
  }

  function confirmar() {
    let pagos = [];
    if (modo === "mixto") {
      if (restante > 0.01) return toast.error(`Faltan $${restante.toFixed(2)} por cubrir`);
      if (sumaMixto - total > 0.01 && cambioMixto < sumaMixto - total - 0.01) return toast.error("Los pagos con tarjeta/transferencia no pueden exceder el total; ajusta los montos");
      let sobrante = cambioMixto;
      pagos = mixto.filter((p) => parseFloat(p.monto) > 0).map((p) => {
        let monto = parseFloat(p.monto);
        if (p.metodo === "efectivo" && sobrante > 0) { const q = Math.min(monto, sobrante); monto = Math.round((monto - q) * 100) / 100; sobrante -= q; }
        return { metodo: p.metodo, monto, descripcion: p.metodo === "personalizado" ? p.descripcion : undefined };
      }).filter((p) => p.monto > 0);
      if (pagos.some((p) => p.metodo === "personalizado" && !p.descripcion?.trim())) {
        return toast.error("El Pago Personalizado requiere descripción");
      }
    } else {
      if (modo === "efectivo" && (parseFloat(recibido) || 0) < total - 0.01) return toast.error("Efectivo insuficiente");
      if (modo === "personalizado" && !descripcion.trim()) return toast.error("Describe el motivo del pago personalizado");
      pagos = [{ metodo: modo, monto: total, referencia: referencia || undefined, descripcion: modo === "personalizado" ? descripcion : undefined }];
    }
    onConfirm(pagos, modo === "efectivo" ? cambio : modo === "mixto" ? cambioMixto : 0);
    reset();
  }

  const tabClass = (activo) =>
    `flex flex-col items-center gap-1.5 p-3 rounded-2xl border-2 text-[11px] font-semibold transition-[border-color,background-color,color,transform] duration-150 active:scale-[0.98] ${
      activo ? "border-brand-amber bg-brand-amber-soft text-brand-ink" : "border-brand-line text-brand-muted hover:border-brand-slate hover:text-brand-ink"
    }`;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onClose(v); }}>
      <DialogContent className="max-w-lg rounded-2xl" data-testid="payment-modal">
        <DialogHeader>
          <div className="eyebrow">Cobro</div>
          <DialogTitle className="font-display text-3xl font-extrabold tracking-tight text-brand-ink">${total.toFixed(2)}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-5 gap-2">
          {METODOS.map(({ id, label, icon: Icon }) => (
            <button key={id} data-testid={`pay-method-${id}`} onClick={() => setModo(id)} className={tabClass(modo === id)}>
              <Icon className={`w-5 h-5 ${modo === id ? "text-brand-amber-dark" : ""}`} />
              {label}
            </button>
          ))}
          <button data-testid="pay-method-mixto" onClick={() => setModo("mixto")} className={tabClass(modo === "mixto")}>
            <Split className={`w-5 h-5 ${modo === "mixto" ? "text-brand-amber-dark" : ""}`} />
            Mixto
          </button>
        </div>

        {modo === "efectivo" && (
          <div className="space-y-2 animate-pop">
            <label className="eyebrow">¿Con cuánto paga?</label>
            <input data-testid="cash-tendered-input" type="number" min="0" step="0.01" value={recibido} onChange={(e) => setRecibido(e.target.value)} placeholder={total.toFixed(2)} className="field h-12 text-lg font-mono" autoFocus />
            <div className="flex gap-1.5">
              <button type="button" data-testid="cash-exact" onClick={() => setRecibido(total.toFixed(2))} className="px-3 h-8 rounded-full text-xs font-bold bg-brand-ink text-white hover:bg-brand-graphite transition-colors duration-150">Exacto</button>
              {DENOMINACIONES.filter((d) => d >= total).slice(0, 3).map((d) => (
                <button key={d} type="button" data-testid={`cash-denom-${d}`} onClick={() => setRecibido(String(d))} className="px-3 h-8 rounded-full text-xs font-bold bg-brand-surface text-brand-slate hover:bg-brand-amber hover:text-brand-ink transition-colors duration-150">${d}</button>
              ))}
            </div>
            <div className="flex justify-between items-center bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
              <span className="text-sm font-medium text-emerald-800">Cambio</span>
              <span data-testid="cash-change-amount" className="font-mono font-bold text-xl text-emerald-800">${cambio.toFixed(2)}</span>
            </div>
          </div>
        )}

        {(modo.startsWith("tarjeta") || modo === "transferencia") && (
          <div className="space-y-2 animate-pop">
            <label className="eyebrow">{modo.startsWith("tarjeta") ? "Referencia de terminal" : "Referencia SPEI / clave"}</label>
            <input data-testid="payment-reference-input" value={referencia} onChange={(e) => setReferencia(e.target.value)} placeholder="Opcional" className="field h-11 font-mono" />
            {modo.startsWith("tarjeta") && <p className="text-xs text-brand-muted">La comisión de terminal se descuenta automáticamente en reportes.</p>}
          </div>
        )}

        {modo === "personalizado" && (
          <div className="space-y-2 animate-pop">
            <label className="eyebrow">Descripción (regalo, creador de contenido, etc.)</label>
            <input data-testid="custom-payment-description" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} placeholder="Ej. Regalo para @creador" className="field h-11" />
          </div>
        )}

        {modo === "mixto" && (
          <div className="space-y-2 animate-pop">
            {mixto.map((p, i) => (
              <div key={i} className="flex gap-2 items-center">
                <select data-testid={`mixto-method-${i}`} value={p.metodo} onChange={(e) => setFilaMixto(i, "metodo", e.target.value)} className="field w-auto px-2">
                  {METODOS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
                <input data-testid={`mixto-amount-${i}`} type="number" min="0" step="0.01" placeholder="$0.00" value={p.monto} onChange={(e) => setFilaMixto(i, "monto", e.target.value)} className="field font-mono" />
                {p.metodo === "personalizado" && (
                  <input data-testid={`mixto-desc-${i}`} placeholder="Descripción" value={p.descripcion} onChange={(e) => setFilaMixto(i, "descripcion", e.target.value)} className="field" />
                )}
                <button data-testid={`mixto-remove-${i}`} onClick={() => setMixto((prev) => prev.filter((_, j) => j !== i))} className="p-2 text-brand-muted hover:text-rose-600 transition-colors duration-150">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button data-testid="mixto-add-row" onClick={() => setMixto([...mixto, { metodo: "tarjeta_debito", monto: restante.toFixed(2), descripcion: "" }])} className="btn-soft h-8 px-3 text-xs">
              <Plus className="w-3.5 h-3.5" /> Agregar método
            </button>
            <div className={`flex justify-between items-center rounded-xl px-4 py-3 border ${restante > 0.01 ? "bg-rose-50 border-rose-200 text-rose-800" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`}>
              <span className="text-sm font-medium">{restante > 0.01 ? "Restante por cubrir" : cambioMixto > 0 ? "Cambio a entregar (efectivo)" : "Cubierto"}</span>
              <span data-testid="mixto-remaining" className="font-mono font-bold text-lg">${restante > 0.01 ? restante.toFixed(2) : cambioMixto.toFixed(2)}</span>
            </div>
          </div>
        )}

        {vendedores.length > 0 && (
          <div className="rounded-2xl border border-brand-line bg-brand-canvas p-3" data-testid="vendedor-selector">
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <div className="eyebrow">Vendedor que atendió {vendedores.some((v) => v.en_turno) ? `· ${vendedores.filter((v) => v.en_turno).length} con entrada checada` : "· sin entrada del checador RubiNova · marca la entrada aquí"}</div>
              {vendedores.some((v) => v.en_turno) && vendedores.some((v) => !v.en_turno) && <button type="button" data-testid="vendedor-toggle-todos" onClick={() => setTodosVend((t) => !t)} className="text-[11px] font-semibold text-brand-amber-dark hover:underline">{todosVend ? "Solo en turno" : "Mostrar todos"}</button>}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {[...vendedores].filter((v) => todosVend || v.en_turno || !vendedores.some((x) => x.en_turno) || v.id === vendedorId).sort((a, b) => Number(b.en_turno) - Number(a.en_turno)).map((v) => (
                <span key={v.id} className="inline-flex items-center">
                  <button type="button" data-testid={`vendedor-chip-${v.id}`} onClick={() => onVendedor?.(vendedorId === v.id ? null : v.id)} className={`h-9 px-3 ${!v.en_turno && onCheckin ? "rounded-l-full" : "rounded-full"} text-xs font-bold flex items-center gap-1.5 transition-colors ${vendedorId === v.id ? "bg-brand-ink text-white" : v.en_turno ? "bg-white border border-brand-amber text-brand-ink hover:bg-brand-amber-soft" : "bg-brand-surface text-brand-muted"}`}>
                    <span className={`w-2 h-2 rounded-full ${v.en_turno ? "bg-emerald-500" : "bg-brand-line"}`} />{v.nombre}{v.en_turno && v.ultimo_checkin && <span className="font-mono opacity-60">{new Date(v.ultimo_checkin).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}</span>}
                  </button>
                  {!v.en_turno && onCheckin && <button type="button" data-testid={`vendedor-checkin-${v.id}`} onClick={() => { setPinPara(v); setPin(""); }} title="Marcar entrada con tu PIN (si el checador RubiNova no la envió)" className="h-9 px-2.5 rounded-r-full text-[11px] font-bold bg-brand-amber-soft text-brand-amber-dark border border-l-0 border-brand-amber-line hover:bg-brand-amber hover:text-brand-ink transition-colors flex items-center gap-1"><LogIn className="w-3 h-3" /> Entrada</button>}
                </span>
              ))}
              {pinPara && (
                <form onSubmit={confirmarPin} data-testid="checkin-pin-form" className="w-full mt-2 flex flex-wrap items-center gap-2 bg-brand-amber-soft/60 border border-brand-amber-line rounded-2xl px-3 py-2">
                  <span className="text-xs text-brand-ink"><b>{pinPara.nombre}</b>, escribe tu PIN para marcar entrada:</span>
                  <input autoFocus data-testid="checkin-pin-input" type="password" inputMode="numeric" maxLength={6} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} className="field h-9 w-28 font-mono text-center tracking-[0.3em]" placeholder="••••" />
                  <button type="submit" data-testid="checkin-pin-submit" disabled={pinBusy || pin.length < 4} className="btn-ink h-9 px-3 text-xs disabled:opacity-50">Marcar entrada</button>
                  <button type="button" data-testid="checkin-pin-cancel" onClick={() => { setPinPara(null); setPin(""); }} className="text-xs text-brand-muted hover:text-brand-ink">Cancelar</button>
                </form>
              )}
            </div>
          </div>
        )}
        <button data-testid="payment-confirm-button" onClick={confirmar} className="btn-amber w-full h-12 text-base">
          Confirmar venta
        </button>
      </DialogContent>
    </Dialog>
  );
}
